import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Building2,
  Network,
  Wrench,
  ClipboardCheck,
  Cable,
  Landmark,
  ArrowRight,
  Phone,
} from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumb from "@/components/Breadcrumb";
import CtaBand from "@/components/CtaBand";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
};

const CIBLES = [
  { icon: Network, title: "Opérateurs télécoms", text: "Renfort de vos équipes de raccordement et de maintenance, en marque blanche ou en transparence." },
  { icon: Building2, title: "Entreprises de déploiement", text: "Capacité de production supplémentaire pour absorber vos campagnes de raccordements FTTH." },
  { icon: Landmark, title: "Grands groupes de travaux publics", text: "Compétences fibre optique spécialisées intégrées à vos lots télécoms." },
  { icon: Wrench, title: "Responsables exploitation & travaux", text: "Un interlocuteur terrain fiable pour vos interventions, dépannages et contrôles." },
];

const PRESTATIONS = [
  { icon: Cable, title: "Raccordement FTTH D3", text: "Raccordement final jusqu'à la prise terminale, en pavillonnaire comme en collectif.", to: "/raccordement-ftth-d3" },
  { icon: Network, title: "Installation & maintenance fibre", text: "Travaux neufs, dépannage et maintenance des réseaux fibre optique professionnels." },
  { icon: ClipboardCheck, title: "Contrôle & inspection", text: "Inspections techniques, audits qualité et remontées VQSE documentées sur vos raccordements.", to: "/qualite-controle" },
  { icon: Wrench, title: "Travaux & dépose cuivre", text: "Interventions sur les réseaux cuivre existants et dépose dans le cadre des arrêts progressifs." },
];

const STEPS = [
  { num: "01", title: "Présentation du besoin", text: "Volumes, zones, référentiels et format de restitution attendu." },
  { num: "02", title: "Étude et cadrage", text: "Analyse de faisabilité, organisation des équipes et calendrier." },
  { num: "03", title: "Contractualisation", text: "Modalités de collaboration définies selon vos exigences achats et qualité." },
  { num: "04", title: "Opérations & reporting", text: "Interventions documentées, remontées terrain et suivi régulier." },
];

const SousTraitance = () => (
  <>
    <Seo
      title="Sous-traitance fibre optique FTTH — opérateurs & TP | ATLAS THD"
      description="ATLAS THD, sous-traitant fibre optique pour opérateurs et donneurs d'ordre : raccordement FTTH D3, installation, maintenance, contrôle qualité et travaux cuivre."
      breadcrumbs={[{ label: "Accueil", to: "/" }, { label: "Sous-traitance fibre optique" }]}
    />

    <section className="border-b border-line bg-cloud" data-testid="soustraitance-hero">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 lg:py-20 lg:px-8">
        <Breadcrumb items={[{ label: "Accueil", to: "/" }, { label: "Sous-traitance fibre optique" }]} />
        <motion.p {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="mt-6 font-display text-xs font-bold uppercase tracking-[0.25em] text-terrain">
          Sous-traitance B2B
        </motion.p>
        <motion.h1 {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }} className="mt-4 max-w-4xl font-display text-4xl font-extrabold tracking-tight text-midnight sm:text-5xl">
          Sous-traitant fibre optique pour opérateurs et donneurs d'ordre
        </motion.h1>
        <motion.p {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mt-6 max-w-3xl text-lg leading-relaxed text-steel">
          ATLAS THD réalise en sous-traitance vos travaux fibre optique :
          raccordements FTTH, installation, maintenance, contrôle qualité et
          travaux sur réseaux cuivre. Des équipes terrain, des interventions
          documentées, une restitution exploitable par vos équipes.
        </motion.p>
        <motion.div {...fadeUp} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.45 }} className="mt-8 flex flex-col gap-3 sm:flex-row">
          <Link to="/contact" data-testid="soustraitance-devis-button" className="group flex items-center justify-center gap-2 bg-ciel px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-midnight hover:text-white">
            Parlez-nous de votre besoin
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
          <a href="tel:+33605765066" data-testid="soustraitance-call-button" className="flex items-center justify-center gap-2 border border-midnight px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-midnight transition-colors duration-200 hover:bg-midnight hover:text-white">
            <Phone className="h-4 w-4" />
            06 05 76 50 66
          </a>
        </motion.div>
      </div>
    </section>

    <section className="bg-white py-20" data-testid="soustraitance-cibles">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">Pour qui</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
            Un partenaire de production pour les professionnels du réseau
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {CIBLES.map((c, i) => (
            <motion.div key={c.title} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} className="bg-white p-8" data-testid={`soustraitance-cible-${i}`}>
              <c.icon className="h-7 w-7 text-terrain" />
              <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-midnight">{c.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-cloud py-20" data-testid="soustraitance-prestations">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">Prestations en sous-traitance</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
            De la production terrain au contrôle qualité
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line md:grid-cols-2">
          {PRESTATIONS.map((p, i) => {
            const inner = (
              <>
                <div className="flex items-center justify-between">
                  <p.icon className="h-8 w-8 text-midnight" />
                  <span className="font-display text-sm font-bold text-line">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-midnight">{p.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-steel">{p.text}</p>
                {p.to && (
                  <span className="mt-4 inline-flex items-center gap-1.5 font-display text-sm font-bold text-terrain">
                    Voir la page dédiée
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                )}
              </>
            );
            return p.to ? (
              <motion.div key={p.title} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} data-testid={`soustraitance-prestation-${i}`}>
                <Link to={p.to} className="block h-full bg-white p-8 transition-colors hover:bg-cloud lg:p-10">{inner}</Link>
              </motion.div>
            ) : (
              <motion.div key={p.title} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} className="bg-white p-8 lg:p-10" data-testid={`soustraitance-prestation-${i}`}>
                {inner}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    <section className="bg-white py-20" data-testid="soustraitance-process">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div {...fadeUp} transition={{ duration: 0.5 }} className="max-w-2xl">
          <p className="font-display text-xs font-bold uppercase tracking-[0.25em] text-steel">Comment démarrer</p>
          <h2 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-midnight sm:text-4xl">
            De votre besoin à la production, en quatre étapes
          </h2>
        </motion.div>
        <div className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <motion.div key={s.num} {...fadeUp} transition={{ duration: 0.4, delay: i * 0.08 }} className="bg-white p-8" data-testid={`soustraitance-step-${i}`}>
              <span className="font-display text-3xl font-extrabold text-terrain">{s.num}</span>
              <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-midnight">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-steel">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    <CtaBand title="Un besoin de sous-traitance fibre optique à confier ?" />
  </>
);

export default SousTraitance;
